import { EventFeedEvent } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { InsightsLivePrice } from '~/components/insights-live-price/InsightsLivePrice';
import { useEntities } from '~/react-query-hooks/entities/useEntities';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { getCardDate } from '~/views/dashboard/whats-new-widget/components/card-base/utils';
import { CardPill } from '~/views/stream/components/stream-card/components/card-pill/CardPill';
import { useCatalystDrawer } from '~/widgets/catalyst-insights-drawer/useCatalystDrawer';
import { useMarketCatalystDrawer } from '~/widgets/market-catalyst-drawer/useMarketCatalystDrawer';

import * as S from '../../OverviewEventFeed.styles';
import { getCatalystEntityTag, parseCatalystData } from './utils';

export interface CatalystRowProps {
  event: EventFeedEvent;
  isLast?: boolean;
  onMarkRead: (eventId: string) => void;
}

export const CatalystRow = ({
  event,
  isLast,
  onMarkRead,
}: CatalystRowProps) => {
  const { t } = useTranslation(['whatsNew', 'insights']);
  const { openDrawer: openCompanyCatalystDrawer } = useCatalystDrawer();
  const { openDrawer: openMarketCatalystDrawer } = useMarketCatalystDrawer();
  const { eventDate, isMarketWide, cardType, themeId, subCategory } =
    parseCatalystData(event);

  const entityTag = getCatalystEntityTag(event);

  const { data: entities } = useEntities({
    entityTags: entityTag ? [entityTag] : [],
    enabled: !!entityTag,
  });
  const entity = entities?.[0];

  const handleClick = () => {
    onMarkRead(event.id);
    if (isMarketWide && subCategory) {
      openMarketCatalystDrawer({
        type: subCategory,
        themeId,
        insightId: event.source_id,
        source: POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW,
      });
    } else {
      openCompanyCatalystDrawer({
        type: 'company',
        entityTag: event.entity,
        insightId: event.source_id,
        source: POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW,
      });
    }
  };

  return (
    <S.FeedItemWrapper $isLast={isLast}>
      <S.FeedItem $isRead={event.read} onClick={handleClick}>
        <S.FeedItemHeader>
          <CardPill type={cardType} isRead={event.read} />
        </S.FeedItemHeader>
        <S.InsightTitle $isRead={event.read}>{event.title}</S.InsightTitle>
        <S.StatsRow>
          <S.StatsContainer>
            {entity && (
              <InsightsLivePrice
                entity={entity}
                isHorizontalView
                showPricePercentage
                showDotSeparator
              />
            )}
          </S.StatsContainer>
          <S.TimeAgo>{getCardDate(eventDate, t)}</S.TimeAgo>
        </S.StatsRow>
      </S.FeedItem>
    </S.FeedItemWrapper>
  );
};
